import { z } from 'zod';
import { HAZARD_KEYS } from './media-signal';
import { MEDIA_SIGNAL_SOURCE_TYPES } from './media-signal-bulletin';

/**
 * ASK THE WIRE (#299) + its mandatory cost cage (#301) — the request/answer
 * contract for the council's on-demand, build-second conversational seam over
 * the wire (docs/news-llm-engine-council-2026-06-15.md, Concept C).
 *
 * An operator types a free-text question; the API retrieves a BOUNDED set of
 * recent signals + briefs + anomalies (the cost lever — the input is never
 * unbounded), then a Haiku-class model answers GROUNDED ONLY in that set, citing
 * click-through evidence, or ABSTAINS honestly ("nothing in the wire about
 * that"). Cite-or-abstain is enforced in CODE, not by a prompt the model could
 * ignore: an answer with zero valid citations, or one whose §3.7 grounding gate
 * fails (an un-sourced place name), never reaches the operator.
 *
 * NEVER auto-urgent, NEVER creates an anomaly, NEVER forwardable as fact — a
 * read-only query surface. On a cap hit or any failure it DEGRADES to a plain
 * filtered list of the retrieved signals (never a dead box, never an ungrounded
 * guess). The cost cage (per-operator/day cap + global daily $ ceiling) lives in
 * the ask_the_wire_queries ledger (migration 0079) and ships WITH this — a
 * free-text box is otherwise an unbounded spend surface.
 */

// ── Request ──────────────────────────────────────────────────────────────────

/** A retrieval-bounding bbox (operator's current focus). Honest-geo only — never
 *  derived from the question text (free-text place names are never geocoded). */
export const AskTheWireBboxSchema = z.object({
  west: z.number().min(-180).max(180),
  south: z.number().min(-90).max(90),
  east: z.number().min(-180).max(180),
  north: z.number().min(-90).max(90),
});
export type AskTheWireBbox = z.infer<typeof AskTheWireBboxSchema>;

export const AskTheWireRequestSchema = z.object({
  /** The operator's free-text question. Bounded length — a question, not a doc. */
  question: z.string().trim().min(3).max(500),
  /** Optional geo bound (the operator's map focus). Omitted ⇒ full wire. */
  bbox: AskTheWireBboxSchema.optional(),
  /** Freshness window in hours on the wire. Default 72h; cap 336h (2 weeks). */
  hours: z.coerce.number().min(1).max(336).optional().default(72),
  /** Optional hazard narrowing (subset of the canonical lexicon keys). The
   *  question text ALSO contributes hazard keys (matchHazardKeywords) server-side. */
  hazard: z.array(z.enum(HAZARD_KEYS)).max(HAZARD_KEYS.length).optional(),
});
export type AskTheWireRequest = z.infer<typeof AskTheWireRequestSchema>;

// ── Evidence (the numbered retrieval set — also the degrade-to-list) ─────────

/** Which wire table an evidence item came from. */
export const ASK_WIRE_EVIDENCE_KINDS = ['signal', 'brief', 'anomaly'] as const;
export type AskWireEvidenceKind = (typeof ASK_WIRE_EVIDENCE_KINDS)[number];

/**
 * One retrieved wire item, numbered for citation. The model is shown EXACTLY
 * this set as [1..N]; the same array is returned to the client so an answer's
 * `[n]` markers map to `evidence[n-1]`, AND so a degraded/abstained response can
 * render the plain list. Click-through = `url` (signals) or the in-app entity
 * the `id`+`kind` resolves to (briefs/anomalies have no external url).
 */
export const AskTheWireEvidenceSchema = z.object({
  /** The wire row's id (media_signals / media_signal_bulletins / _anomalies). */
  id: z.string(),
  kind: z.enum(ASK_WIRE_EVIDENCE_KINDS),
  /** Upstream feed / outlet / cluster source (gdelt, rss, bluesky, x, or a worker tag). */
  source: z.string(),
  /** Distinct-origin identity (outlet domain / handle / feed), when known. */
  origin: z.string().nullable(),
  title: z.string().nullable(),
  /** External click-through, when the item has one (news/social/official signals). */
  url: z.string().nullable(),
  /** ISO-8601 observation time (COALESCE(published_at, ingested_at) / generated_at). */
  observedAt: z.string(),
  geoName: z.string().nullable(),
  hazardKey: z.string().nullable(),
  /** news / social / official / scanner — when the item carries a source TYPE. */
  sourceType: z.enum(MEDIA_SIGNAL_SOURCE_TYPES).nullable(),
});
export type AskTheWireEvidence = z.infer<typeof AskTheWireEvidenceSchema>;

// ── Answer ───────────────────────────────────────────────────────────────────

/** answered = grounded, cited prose. abstained = nothing in the wire answers it
 *  (deterministic on an empty set, or the model declined). degraded = a cap hit
 *  or a failure fell back to the plain list (the cost cage / honesty fallback). */
export const ASK_WIRE_STATUSES = ['answered', 'abstained', 'degraded'] as const;
export type AskTheWireStatus = (typeof ASK_WIRE_STATUSES)[number];

/** Why a degraded response fell back to the list (null unless degraded). */
export const ASK_WIRE_DEGRADE_REASONS = [
  'operator_cap', // per-operator/day query cap reached (#301)
  'global_cap', // global daily $ ceiling reached (#301 hard backstop)
  'llm_disabled', // no ANTHROPIC_API_KEY configured — the seam is read-only-list
  'llm_error', // the model call threw / returned empty
  'grounding_withheld', // §3.7 gate caught an un-sourced place name in the answer
] as const;
export type AskTheWireDegradeReason = (typeof ASK_WIRE_DEGRADE_REASONS)[number];

/** Cost-cage chrome — what the operator has left today (#301). */
export const AskTheWireBudgetSchema = z.object({
  /** Queries this operator may still spend today (cap − used; ≥0). */
  operatorRemaining: z.number().int().nonnegative(),
  /** USD still available under the global daily ceiling (≥0). */
  globalRemainingUsd: z.number().nonnegative(),
  perOperatorCap: z.number().int().positive(),
  globalCeilingUsd: z.number().positive(),
});
export type AskTheWireBudget = z.infer<typeof AskTheWireBudgetSchema>;

export const AskTheWireAnswerSchema = z.object({
  status: z.enum(ASK_WIRE_STATUSES),
  /** Grounded prose with `[n]` citation markers — answered only; null otherwise. */
  answer: z.string().nullable(),
  /** True when nothing in the wire answers the question (honest abstain). */
  abstained: z.boolean(),
  /** True when a cap/failure fell back to the plain list. */
  degraded: z.boolean(),
  degradeReason: z.enum(ASK_WIRE_DEGRADE_REASONS).nullable(),
  /** The always-present honesty line ("Unconfirmed signal intelligence…" / the
   *  abstain / the degrade message) — never auto-urgent, never presented as fact. */
  notConfirmedLine: z.string(),
  /** The numbered retrieval set (model input + degrade-to-list). May be empty. */
  evidence: z.array(AskTheWireEvidenceSchema),
  /** Ids within `evidence` the answer actually cited (answered only; else []). */
  citationIds: z.array(z.string()),
  /** Size of the bounded retrieval set the model saw. */
  retrievedCount: z.number().int().nonnegative(),
  /** Which model wrote the answer (null on abstain-without-LLM / degrade). */
  model: z.string().nullable(),
  /** This query's billed cost in USD (0 on a free abstain / degrade). */
  costUsd: z.number().nonnegative(),
  budget: AskTheWireBudgetSchema,
});
export type AskTheWireAnswer = z.infer<typeof AskTheWireAnswerSchema>;
